package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.Cancellable
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyApplicationStatus
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentKind
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceEventType
import com.elan.edubridge.shared.domain.model.AttendanceSource
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceSummary
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ClassProgressState
import com.elan.edubridge.shared.domain.model.ClassSchedule
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.ContentReportReason
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportStatus
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.ContentStatus
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.EventScope
import com.elan.edubridge.shared.domain.model.GuardianContact
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.LinkStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.ProblemBankSpec
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.TodayClass

/**
 * 데모·테스트용 카탈로그. **학원 2곳이 서로 완전히 다른 데이터를 갖는다.**
 *
 * 같은 리스트를 모든 `academyId`에 돌려주면 테넌시 테스트가 통과하면서도 실제 누수를 못 잡는다.
 * 그래서 모든 조회가 `academyId`로 필터링하고, 모르는 학원은 빈 목록을 돌려준다.
 *
 * 학생 단위 필터(`studentUserId`)는 흉내내지 않는다 — 개인정보 격리는 미러 **경로**가 보장하는
 * 성질이고(§4.3) 그 경계는 [myTimeline]이 표현한다. 카탈로그는 학원 범위까지만 책임진다.
 */
class InMemoryEduConnectDataSource : EduConnectDataSource {

    override suspend fun academies(academyIds: List<String>): List<AcademyData> =
        ACADEMIES.filter { it.id in academyIds }

    override suspend fun classes(academyId: String): List<ClassData> =
        CLASSES.filter { it.academyId == academyId }

    override suspend fun students(academyId: String): List<StudentData> =
        STUDENTS.filter { it.academyId == academyId }

    override suspend fun notices(academyId: String): List<NoticeData> =
        NOTICES.filter { it.academyId == academyId }

    override suspend fun posts(academyId: String): List<PostData> =
        POSTS.filter { it.academyId == academyId }

    override suspend fun post(academyId: String, postId: String): PostData? =
        POSTS.firstOrNull { it.academyId == academyId && it.id == postId }

    override suspend fun postComments(academyId: String, postId: String): List<PostComment> =
        COMMENTS.filter { it.academyId == academyId && it.postId == postId }

    /** 서버와 같이 `startAt` 한 필드로만 자른다 — 양끝 포함이고 문자열 비교가 곧 시각 비교다. */
    override suspend fun academyEvents(
        academyId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = ACADEMY_EVENTS.filter {
        it.academyId == academyId && it.startAt >= startAt && it.startAt <= endAt
    }

    override suspend fun personalEvents(
        userId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = PERSONAL_EVENTS_BY_USER[userId].orEmpty()
        .filter { it.startAt >= startAt && it.startAt <= endAt }

    override suspend fun studentSchedule(
        academyId: String,
        studentUserId: String,
    ): List<ClassData> = classes(academyId)

    override suspend fun studentAssignments(
        academyId: String,
        studentUserId: String,
    ): List<Assignment> = ASSIGNMENTS.filter { it.academyId == academyId }

    override suspend fun todayClasses(academyId: String, serviceDate: String?): List<TodayClass> =
        TODAY_CLASSES.filter { it.academyId == academyId }

    override suspend fun attendanceSummary(
        academyId: String,
        serviceDate: String?,
    ): AttendanceSummary {
        val roster = ROSTER.filter { it.academyId == academyId }
        return AttendanceSummary(
            academyId = academyId,
            serviceDate = serviceDate ?: DEMO_SERVICE_DATE,
            classCount = roster.map { it.classId }.distinct().size,
            checkedInCount = roster.count {
                it.status == AttendanceStatus.PRESENT || it.status == AttendanceStatus.LATE
            },
            checkedOutCount = roster.count { it.status == AttendanceStatus.LEFT },
            notArrivedCount = roster.count { it.status == AttendanceStatus.NOT_ARRIVED },
        )
    }

    override suspend fun dailyRoster(
        academyId: String,
        serviceDate: String?,
        classId: String?,
    ): List<DailyRosterEntry> = ROSTER.filter {
        it.academyId == academyId &&
            (serviceDate == null || it.serviceDate == serviceDate) &&
            (classId == null || it.classId == classId)
    }

    /** `serviceDate`가 `yyyy-MM-dd`로 정규화돼 있어 사전순 비교가 곧 날짜순 비교다(R12). */
    override suspend fun attendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
    ): List<DailyRosterEntry> = ROSTER.filter {
        it.academyId == academyId &&
            it.serviceDate >= startDate &&
            it.serviceDate <= endDate &&
            (classId == null || it.classId == classId) &&
            (studentId == null || it.studentId == studentId)
    }

    override suspend fun myTimeline(
        userId: String,
        academyId: String?,
        subjectStudentId: String?,
        limit: Int,
    ): List<AttendanceTimelineEntry> = TIMELINE_BY_USER[userId].orEmpty()
        .filter {
            (academyId == null || it.academyId == academyId) &&
                (subjectStudentId == null || it.subjectStudentId == subjectStudentId)
        }
        .take(limit)

    override suspend fun guardianLinks(guardianUserId: String): List<GuardianLink> =
        GUARDIAN_LINKS.filter { it.guardianUserId == guardianUserId }

    override suspend fun studentGuardianLinks(
        academyId: String,
        studentId: String,
    ): List<GuardianLink> = GUARDIAN_LINKS.filter {
        it.academyId == academyId && it.studentId == studentId
    }

    override suspend fun assignments(academyId: String): List<Assignment> =
        ASSIGNMENTS.filter { it.academyId == academyId }

    override suspend fun assignmentSubmissions(
        academyId: String,
        assignmentId: String,
    ): List<AssignmentSubmission> = SUBMISSIONS.filter {
        it.academyId == academyId && it.assignmentId == assignmentId
    }

    override suspend fun pendingAcademyApplications(): List<AcademyApplication> =
        APPLICATIONS.filter { it.status == AcademyApplicationStatus.PENDING }

    override suspend fun myAcademyApplications(applicantUid: String): List<AcademyApplication> =
        APPLICATIONS.filter { it.applicantUid == applicantUid }.sortedByDescending { it.createdAt }

    override suspend fun contentReports(
        academyId: String,
        openOnly: Boolean,
    ): List<ContentReport> =
        CONTENT_REPORTS
            .filter { it.academyId == academyId && (!openOnly || it.isOpen) }
            .sortedByDescending { it.createdAt }

    /** 데모 피드는 스냅샷 하나다 — 한 번 내보내고 끝낸다(이전 `flowOf`와 같은 동작). */
    override fun observeAttendanceEvents(
        academyId: String,
        limit: Int,
        onEach: (List<AttendanceEvent>) -> Unit,
        onComplete: () -> Unit,
    ): Cancellable {
        onEach(EVENTS.filter { it.academyId == academyId }.take(limit))
        onComplete()
        return Cancellable {}
    }
}

internal const val DEMO_ACADEMY_ID = "academy-demo"
internal const val SECOND_ACADEMY_ID = "academy-two"
private const val DEMO_SERVICE_DATE = "2026-08-03"

private val ACADEMIES = listOf(
    AcademyData(
        id = DEMO_ACADEMY_ID,
        name = "ABC 수학학원",
        description = "중고등 수학 전문 학원",
        phone = "02-1234-5678",
        address = "서울시 강남구 테헤란로 123",
        studentCount = 25,
        teacherCount = 3,
    ),
    AcademyData(
        id = SECOND_ACADEMY_ID,
        name = "XYZ 영어학원",
        description = "영어 회화 및 문법 전문 학원",
        phone = "02-2345-6789",
        address = "서울시 서초구 서초대로 456",
        studentCount = 18,
        teacherCount = 2,
    ),
)

private val CLASSES = listOf(
    ClassData(
        id = "class-math-1",
        academyId = DEMO_ACADEMY_ID,
        className = "A반",
        subject = "고1 수학",
        teacher = "김선생님",
        schedule = ClassSchedule(daysOfWeek = listOf(1, 3, 5), 19 * 60, 21 * 60),
        studentCount = 15,
    ),
    ClassData(
        id = "class-math-2",
        academyId = DEMO_ACADEMY_ID,
        className = "C반",
        subject = "중3 과학",
        teacher = "박선생님",
        schedule = ClassSchedule(daysOfWeek = listOf(6), 14 * 60, 16 * 60),
        studentCount = 18,
    ),
    ClassData(
        id = "class-eng-1",
        academyId = SECOND_ACADEMY_ID,
        className = "B반",
        subject = "고2 영어",
        teacher = "이선생님",
        schedule = ClassSchedule(daysOfWeek = listOf(2, 4), 18 * 60, 20 * 60),
        studentCount = 12,
    ),
)

private val STUDENTS = listOf(
    StudentData(
        id = "student-1",
        academyId = DEMO_ACADEMY_ID,
        userId = "student",
        name = "김학생",
        grade = "고1",
        phone = "010-1234-5678",
        guardianContacts = listOf(GuardianContact("김보호", "010-9876-5432", "모")),
    ),
    StudentData(
        id = "student-2",
        academyId = DEMO_ACADEMY_ID,
        name = "박학생",
        grade = "중3",
        phone = "010-3456-7890",
        guardianContacts = listOf(GuardianContact("박보호", "010-7654-3210", "부")),
    ),
    StudentData(
        id = "student-3",
        academyId = SECOND_ACADEMY_ID,
        name = "이학생",
        grade = "고2",
        phone = "010-2345-6789",
        guardianContacts = listOf(GuardianContact("이보호", "010-8765-4321", "모")),
    ),
)

/**
 * 마지막 한 건은 **툼스톤**이다. 서버가 삭제 시 제목·본문을 비우고 문서를 남기는 모양을 그대로
 * 흉내 낸 것으로, ACTIVE 필터가 빠지면 목록에 빈 카드가 나타나 즉시 드러난다.
 */
private val NOTICES = listOf(
    NoticeData("notice-1", DEMO_ACADEMY_ID, "수업 일정 안내", "2026-08-01", "8월 수업 일정을 확인해 주세요."),
    NoticeData("notice-2", DEMO_ACADEMY_ID, "학부모 상담 안내", "2026-07-28", "상담 신청이 시작되었습니다."),
    NoticeData("notice-3", SECOND_ACADEMY_ID, "여름 특강 안내", "2026-07-25", "영어 특강을 준비하고 있습니다."),
    NoticeData(
        id = "notice-deleted",
        academyId = DEMO_ACADEMY_ID,
        title = "",
        date = "2026-07-20",
        content = "",
        status = ContentStatus.DELETED,
    ),
)

/**
 * 게시판 데모. 목록은 최신순으로 뒤집혀 나가야 하므로 여기서는 **일부러 뒤섞어** 둔다 —
 * 데이터소스 순서를 그대로 흘리는 구현이면 정렬 테스트가 잡는다.
 */
private val POSTS = listOf(
    PostData(
        id = "post-1",
        academyId = DEMO_ACADEMY_ID,
        title = "8월 모의고사 안내",
        content = "8월 셋째 주 토요일에 모의고사를 봅니다.",
        authorUid = "teacher",
        authorName = "김선생님",
        commentCount = 1,
        attachments = listOf(
            PostAttachment(
                id = "attachment-1",
                fileName = "모의고사_시간표.pdf",
                contentType = AttachmentContentType.PDF,
                sizeBytes = 248_000,
                objectPath = "edubridge/academies/$DEMO_ACADEMY_ID/posts/post-1/attachment-1",
                createdAt = "2026-08-02T09:10:00Z",
                createdBy = "teacher",
            ),
        ),
        createdAt = "2026-08-02T09:00:00Z",
    ),
    PostData(
        id = "post-3",
        academyId = DEMO_ACADEMY_ID,
        title = "여름 방학 자습실 운영",
        content = "자습실은 평일 10시부터 22시까지 엽니다.",
        authorUid = "owner",
        authorName = "원장님",
        createdAt = "2026-08-05T11:00:00Z",
    ),
    PostData(
        id = "post-deleted",
        academyId = DEMO_ACADEMY_ID,
        title = "",
        content = "",
        status = ContentStatus.DELETED,
        authorUid = "teacher",
        authorName = "김선생님",
        createdAt = "2026-08-04T08:00:00Z",
    ),
    PostData(
        id = "post-2",
        academyId = SECOND_ACADEMY_ID,
        title = "원어민 회화반 개설",
        content = "9월부터 원어민 회화반을 엽니다.",
        authorUid = "teacher-two",
        authorName = "이선생님",
        createdAt = "2026-07-30T10:00:00Z",
    ),
)

/** 삭제된 댓글도 남는다 — `commentCount`는 서버가 감산하므로 ACTIVE 수와 일치한다. */
private val COMMENTS = listOf(
    PostComment(
        id = "comment-1",
        postId = "post-1",
        academyId = DEMO_ACADEMY_ID,
        content = "시험 범위도 알려 주세요.",
        authorUid = "parent",
        authorName = "김보호",
        createdAt = "2026-08-02T10:00:00Z",
    ),
    PostComment(
        id = "comment-deleted",
        postId = "post-1",
        academyId = DEMO_ACADEMY_ID,
        content = "",
        status = ContentStatus.DELETED,
        authorUid = "student",
        authorName = "김학생",
        createdAt = "2026-08-02T09:30:00Z",
    ),
)

/**
 * 학원 캘린더 데모. **일부러 시작 시각 순서를 뒤섞어** 둔다 — 데이터소스 순서를 그대로 흘리는
 * 구현이면 정렬 테스트가 잡는다. 마지막 한 건은 게시글과 같은 모양의 **툼스톤**이라
 * ACTIVE 필터가 빠지면 달력에 빈 칸이 나타난다.
 */
private val ACADEMY_EVENTS = listOf(
    CalendarEvent(
        id = "event-midterm",
        scope = EventScope.ACADEMY,
        academyId = DEMO_ACADEMY_ID,
        title = "8월 모의고사",
        description = "고3 전체 대상입니다.",
        startAt = "2026-08-20T01:00:00.000Z",
        endAt = "2026-08-20T04:00:00.000Z",
        createdBy = "teacher",
        createdByName = "김선생님",
        createdAt = "2026-08-01T09:00:00.000Z",
    ),
    CalendarEvent(
        id = "event-holiday",
        scope = EventScope.ACADEMY,
        academyId = DEMO_ACADEMY_ID,
        title = "개교기념일 휴원",
        // 종일 일정도 진짜 순간을 갖는다 — allDay는 "시:분을 그리지 말라"는 표시일 뿐이다.
        startAt = "2026-08-14T15:00:00.000Z",
        endAt = "2026-08-15T14:59:59.999Z",
        allDay = true,
        createdBy = "owner",
        createdByName = "원장님",
        createdAt = "2026-08-01T09:10:00.000Z",
    ),
    CalendarEvent(
        id = "event-deleted",
        scope = EventScope.ACADEMY,
        academyId = DEMO_ACADEMY_ID,
        title = "",
        description = "",
        startAt = "2026-08-18T01:00:00.000Z",
        endAt = "2026-08-18T02:00:00.000Z",
        status = ContentStatus.DELETED,
        createdBy = "teacher",
        createdByName = "김선생님",
        createdAt = "2026-08-01T09:20:00.000Z",
    ),
    CalendarEvent(
        id = "event-eng-open",
        scope = EventScope.ACADEMY,
        academyId = SECOND_ACADEMY_ID,
        title = "원어민 회화반 설명회",
        startAt = "2026-08-22T08:00:00.000Z",
        endAt = "2026-08-22T09:00:00.000Z",
        createdBy = "teacher-two",
        createdByName = "이선생님",
        createdAt = "2026-07-30T10:00:00.000Z",
    ),
)

/**
 * 개인 캘린더는 uid로 나뉜다. 학원 일정과 달리 `academyId`가 없고, 다른 사람의 uid로는
 * 조회할 경로 자체가 없다 — [TIMELINE_BY_USER]와 같은 구조다.
 */
private val PERSONAL_EVENTS_BY_USER = mapOf(
    "student" to listOf(
        CalendarEvent(
            id = "event-dentist",
            scope = EventScope.PERSONAL,
            title = "치과 예약",
            startAt = "2026-08-19T05:00:00.000Z",
            endAt = "2026-08-19T06:00:00.000Z",
            createdBy = "student",
            createdAt = "2026-08-10T02:00:00.000Z",
        ),
        CalendarEvent(
            id = "event-study",
            scope = EventScope.PERSONAL,
            title = "독서실",
            startAt = "2026-08-16T10:00:00.000Z",
            endAt = "2026-08-16T13:00:00.000Z",
            createdBy = "student",
            createdAt = "2026-08-10T02:05:00.000Z",
        ),
    ),
)

private val ASSIGNMENTS = listOf(
    Assignment("assign-1", DEMO_ACADEMY_ID, "class-math-1", "수학 문제집 3장", "김선생님", "2026-08-04", false),
    Assignment("assign-2", DEMO_ACADEMY_ID, "class-math-2", "과학 실험 보고서", "박선생님", "2026-08-09", true),
    Assignment("assign-3", SECOND_ACADEMY_ID, "class-eng-1", "영어 단어 시험", "이선생님", "2026-08-07", false),
    // 소프트 삭제 툼스톤. 공지·게시글과 같은 이유로 표본에 남긴다 — 필터가 빠졌을 때
    // 빈 제목의 과제가 목록에 뜨는 것을 `BoardContentTest`가 잡는다.
    Assignment(
        id = "assign-deleted",
        academyId = DEMO_ACADEMY_ID,
        classId = "class-math-1",
        title = "",
        teacher = "김선생님",
        dueDate = "2026-08-02",
        status = ContentStatus.DELETED,
    ),
    // 문제 은행 과제. 반을 지정하지 않은 학원 전체 과제이기도 하다(classId = null).
    Assignment(
        id = "assign-bank-1",
        academyId = DEMO_ACADEMY_ID,
        classId = null,
        title = "중2 수학 연산 10문항",
        teacher = "김선생님",
        dueDate = "2026-08-12",
        kind = AssignmentKind.PROBLEM_BANK,
        problemBank = ProblemBankSpec(
            subject = "수학",
            grade = "중2",
            difficulty = "보통",
            questionCount = 10,
            combinationKey = "중2_수학_보통",
        ),
    ),
)

private val SUBMISSIONS = listOf(
    AssignmentSubmission(
        assignmentId = "assign-bank-1",
        academyId = DEMO_ACADEMY_ID,
        studentId = "student-1",
        studentUserId = "student",
        studentName = "김학생",
        correct = 8,
        total = 10,
        scorePercent = 80,
        elapsedSeconds = 245,
        submittedAt = "2026-08-11T09:20:00.000Z",
    ),
)

private val TODAY_CLASSES = listOf(
    TodayClass(
        classId = "class-math-1",
        academyId = DEMO_ACADEMY_ID,
        className = "A반",
        subject = "고1 수학",
        teacher = "김선생님",
        schedule = ClassSchedule(daysOfWeek = listOf(1, 3, 5), 19 * 60, 21 * 60),
        enrolledCount = 15,
        checkedInCount = 12,
        state = ClassProgressState.IN_PROGRESS,
    ),
    TodayClass(
        classId = "class-eng-1",
        academyId = SECOND_ACADEMY_ID,
        className = "B반",
        subject = "고2 영어",
        teacher = "이선생님",
        schedule = ClassSchedule(daysOfWeek = listOf(2, 4), 18 * 60, 20 * 60),
        enrolledCount = 12,
        checkedInCount = 0,
        state = ClassProgressState.SCHEDULED,
    ),
)

private val ROSTER = listOf(
    DailyRosterEntry(
        academyId = DEMO_ACADEMY_ID,
        serviceDate = DEMO_SERVICE_DATE,
        classId = "class-math-1",
        className = "A반",
        studentId = "student-1",
        studentName = "김학생",
        status = AttendanceStatus.PRESENT,
        lastEventId = "event-1",
        lastEventAt = "2026-08-03T19:02:00+09:00",
        // 등원만 한 상태 — 하원 시각은 CHECK_OUT에서야 채워진다.
        checkInAt = "2026-08-03T19:02:00+09:00",
    ),
    DailyRosterEntry(
        academyId = DEMO_ACADEMY_ID,
        serviceDate = DEMO_SERVICE_DATE,
        classId = "class-math-2",
        className = "C반",
        studentId = "student-2",
        studentName = "박학생",
        status = AttendanceStatus.NOT_ARRIVED,
    ),
    DailyRosterEntry(
        academyId = SECOND_ACADEMY_ID,
        serviceDate = DEMO_SERVICE_DATE,
        classId = "class-eng-1",
        className = "B반",
        studentId = "student-3",
        studentName = "이학생",
        status = AttendanceStatus.LATE,
        lateArrival = true,
        lastEventId = "event-2",
        lastEventAt = "2026-08-03T18:12:00+09:00",
        checkInAt = "2026-08-03T18:12:00+09:00",
    ),
)

private val EVENTS = listOf(
    AttendanceEvent(
        eventId = "event-1",
        academyId = DEMO_ACADEMY_ID,
        studentId = "student-1",
        studentName = "김학생",
        classId = "class-math-1",
        className = "A반",
        type = AttendanceEventType.CHECK_IN,
        status = AttendanceStatus.PRESENT,
        occurredAt = "2026-08-03T19:02:00+09:00",
        serviceDate = DEMO_SERVICE_DATE,
        source = AttendanceSource.QR,
        actorUid = "student",
        sessionId = "qr-session-1",
    ),
    AttendanceEvent(
        eventId = "event-2",
        academyId = SECOND_ACADEMY_ID,
        studentId = "student-3",
        studentName = "이학생",
        classId = "class-eng-1",
        className = "B반",
        type = AttendanceEventType.CHECK_IN,
        status = AttendanceStatus.LATE,
        occurredAt = "2026-08-03T18:12:00+09:00",
        serviceDate = DEMO_SERVICE_DATE,
        source = AttendanceSource.MANUAL,
        actorUid = "teacher",
    ),
)

/**
 * 미러는 uid로 나뉜다. 학생 본인과 ACTIVE 링크 보호자만 문서를 갖고,
 * 다른 학생의 uid로는 조회할 경로 자체가 없다(§4.3).
 */
private val TIMELINE_BY_USER = mapOf(
    "student" to listOf(
        AttendanceTimelineEntry(
            eventId = "event-1",
            subjectStudentId = "student-1",
            subjectStudentName = "김학생",
            academyId = DEMO_ACADEMY_ID,
            academyName = "ABC 수학학원",
            classId = "class-math-1",
            className = "A반",
            status = AttendanceStatus.PRESENT,
            occurredAt = "2026-08-03T19:02:00+09:00",
            serviceDate = DEMO_SERVICE_DATE,
            source = AttendanceSource.QR,
        ),
    ),
    "parent" to listOf(
        AttendanceTimelineEntry(
            eventId = "event-1",
            subjectStudentId = "student-1",
            subjectStudentName = "김학생",
            academyId = DEMO_ACADEMY_ID,
            academyName = "ABC 수학학원",
            classId = "class-math-1",
            className = "A반",
            status = AttendanceStatus.PRESENT,
            occurredAt = "2026-08-03T19:02:00+09:00",
            serviceDate = DEMO_SERVICE_DATE,
            source = AttendanceSource.QR,
        ),
    ),
)

private val GUARDIAN_LINKS = listOf(
    GuardianLink(
        academyId = DEMO_ACADEMY_ID,
        guardianUserId = "parent",
        studentId = "student-1",
        studentUserId = "student",
        studentName = "김학생",
        relation = "모",
        status = LinkStatus.ACTIVE,
        linkedAt = "2026-03-01T09:00:00Z",
    ),
    // 한 학생에 보호자가 여럿일 수 있고, 해지 이력도 학생 상세에는 남는다.
    GuardianLink(
        academyId = DEMO_ACADEMY_ID,
        guardianUserId = "parent-former",
        studentId = "student-1",
        studentUserId = "student",
        studentName = "김학생",
        relation = "부",
        status = LinkStatus.REVOKED,
        linkedAt = "2026-03-01T09:00:00Z",
    ),
    GuardianLink(
        academyId = SECOND_ACADEMY_ID,
        guardianUserId = "parent-two",
        studentId = "student-3",
        studentName = "이학생",
        relation = "모",
        status = LinkStatus.ACTIVE,
        linkedAt = "2026-04-01T09:00:00Z",
    ),
)

private val APPLICATIONS = listOf(
    AcademyApplication(
        id = "application-1",
        academyName = "새싹 영어학원",
        applicantUid = "applicant-1",
        contactPhone = "02-4567-8901",
        status = AcademyApplicationStatus.PENDING,
        createdAt = "2026-08-02T10:00:00Z",
    ),
    AcademyApplication(
        id = "application-2",
        academyName = "ABC 수학학원",
        applicantUid = "owner",
        contactPhone = "02-1234-5678",
        status = AcademyApplicationStatus.APPROVED,
        createdAt = "2026-02-20T10:00:00Z",
        academyId = DEMO_ACADEMY_ID,
        decidedBy = "admin",
        decidedAt = "2026-03-01T09:00:00Z",
    ),
)

/**
 * 신고 대기열 픽스처. 세 행이 각각 다른 축을 덮는다 — 대상 종류(글/댓글/사람),
 * 상태(OPEN/RESOLVED), 그리고 `reportCount > 1`인 재신고. 대기열 화면이 셋을 서로
 * 다르게 그려야 하므로 하나라도 빠지면 빈 화면과 구분되지 않는 상태로 개발하게 된다.
 */
private val CONTENT_REPORTS = listOf(
    ContentReport(
        id = "POST_post-1_member-2",
        academyId = DEMO_ACADEMY_ID,
        targetType = ContentReportTargetType.POST,
        targetId = "post-1",
        targetAuthorUid = "member-1",
        reason = ContentReportReason.PRIVACY,
        detail = "본문에 다른 학생 전화번호가 적혀 있어요.",
        reporterUid = "member-2",
        status = ContentReportStatus.OPEN,
        createdAt = "2026-08-11T09:12:00Z",
    ),
    ContentReport(
        id = "COMMENT_comment-2_member-3",
        academyId = DEMO_ACADEMY_ID,
        targetType = ContentReportTargetType.COMMENT,
        targetId = "comment-2",
        postId = "post-2",
        targetAuthorUid = "member-4",
        reason = ContentReportReason.ABUSE,
        reporterUid = "member-3",
        status = ContentReportStatus.OPEN,
        reportCount = 2,
        createdAt = "2026-08-10T14:03:00Z",
    ),
    ContentReport(
        id = "USER_member-5_member-1",
        academyId = DEMO_ACADEMY_ID,
        targetType = ContentReportTargetType.USER,
        targetId = "member-5",
        targetAuthorUid = "member-5",
        reason = ContentReportReason.SPAM,
        reporterUid = "member-1",
        status = ContentReportStatus.RESOLVED,
        resolution = ContentReportResolution.DISMISSED,
        resolutionNote = "학원 공식 계정이라 광고가 아닙니다.",
        resolvedAt = "2026-08-09T02:40:00Z",
        resolvedBy = "owner",
        createdAt = "2026-08-08T23:55:00Z",
    ),
)
